<script setup>
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import { computed, onMounted, ref } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";


// Icon
import IconBlueBox from '@/components/icons/IconBlueBox.vue'


import RequestMaterial from "@/components/shared/Material/RequestMaterial.vue";
import RequestMaterialReturn from "@/components/shared/Material/RequestMaterialReturn.vue";
import MyMaterialDialog from "@/components/shared/Material/MyMaterialDialog.vue";
import MaterialDeliveryDialog from "@/components/shared/Material/MaterialDeliveryDialog.vue";
import ConfirmMaterialDialog from "@/components/shared/Material/ConfirmMaterialDialog.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";

// Services
import MaterialService from '@/api/services/materialAssignment.service';
import MaterialRequestService from '@/api/services/materialRequest.service';
import MaterialTypeService from '@/api/services/materialType.service';

const authStore = useAuthStore();
const uiStore = useUiStore();

const filteredMaterial = ref([]);
const filteredRequests = ref([]);
const materialTypes = ref([]);
const totalResults = ref(0);
const totalRequestResults = ref(0);

const page = ref(1)
const itemsPerPage = ref(30);
const requestPage = ref(1);
const requestItemsPerPage = ref(30);
const pageSizeOptions = [10, 20, 30, 50, 100, 200];
const params = ref({});
const selectedMaterial = ref();

// eslint-disable-next-line no-unused-vars
const user = computed(() => {
    return authStore.getUserLogged;
})

const status = ref('all');
const requestMaterialTypeFilter = ref();
const requestStatusFilter = ref();
const requestStatusOptions = ref([
    {
        name: 'Pendiente',
        value: 'pending',
    },
    {
        name: 'Aprobada',
        value: 'approved',
    },
    {
        name: 'Rechazada',
        value: 'rejected',
    },
    {
        name: 'Completada',
        value: 'fulfilled',
    },
]);

const filter = ref([{
    name: "Todos",
    active: true,
}, {
    name: "Entregado",
    active: false,
}, {
    name: "Devoluciones",
    active: false,
}])

const requestMaterial = ref(false)
const requestMaterialReturn = ref(false)
const dialogMyMaterial = ref(false)
const dialogConfirmMaterial = ref(false)
const materialDelivery = ref(false)

onMounted(() => {
    getMaterials();
    getRequests();
    getMaterialsTypes();
})

const buildMaterialParams = () => ({
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    status: status.value,
})

const changeFilter = (index) => {
    let temp = "";
    filter.value.forEach((item, i) => {
        item.active = i === index
        if (item.active) {
            temp = item.name
        }
    })
    let nextStatus = status.value;
    switch (temp) {
        case "Todos":
            nextStatus = "all"
            break;
        case "Entregado":
            nextStatus = "assigned"
            break;
        case "Devoluciones":
            nextStatus = "returned"
            break;
        default:
            break;
    }
    status.value = nextStatus;
    page.value = 1;

    getMaterials();
}


const closeDialogRequestMaterial = (temp = null) => {
    if (temp) {
        getRequests();
    }
    requestMaterial.value = false;
    selectedMaterial.value = null;
}
const closeDialogRequestMaterialReturn = (temp = null) => {
    if (temp) {
        getMaterials();
    }
    requestMaterialReturn.value = false;
    selectedMaterial.value = null;
}
const closeDialogMyMaterial = () => {
    dialogMyMaterial.value = false;
    selectedMaterial.value = null;

}
const closeDialogConfirmMaterial = (temp = null) => {
    if (temp) {
        getMaterials();
    }
    dialogConfirmMaterial.value = false;
    selectedMaterial.value = null;
}

const openDialogConfirmMaterial = (material) => {
    selectedMaterial.value = material;
    dialogConfirmMaterial.value = true;
}

const openDialogReturnMaterial = (material) => {
    selectedMaterial.value = material;
    requestMaterialReturn.value = true;
}

const openDialogInfoMaterial = (material) => {
    selectedMaterial.value = material;
    materialDelivery.value = true;
}
const closeDialogInfoMaterial = (saved = false) => {
    if (saved) getMaterials();
    selectedMaterial.value = null;
    materialDelivery.value = false;
}

const onMaterialPageChange = (currentPage) => {
    page.value = currentPage;
    getMaterials();
}

const onMaterialSizeChange = (pageSize) => {
    itemsPerPage.value = pageSize;
    page.value = 1;
    getMaterials();
}

const searchRequests = () => {
    requestPage.value = 1;
    getRequests();
}

const onRequestPageChange = (currentPage) => {
    requestPage.value = currentPage;
    getRequests();
}

const onRequestSizeChange = (pageSize) => {
    requestItemsPerPage.value = pageSize;
    requestPage.value = 1;
    getRequests();
}

const getMaterials = async () => {
    params.value = buildMaterialParams();
    uiStore.startLoading();
    try {
        const res = await MaterialService.getMaterialsEmployee(user.value.id, params.value);
        if (res && res.status === 200) {
            totalResults.value = res.data['hydra:totalItems'];
            filteredMaterial.value = res.data['hydra:member'];
        }
    } finally {
        uiStore.stopLoading();
    }
}

const getRequests = () => {
    const requestParams = {
        page: requestPage.value,
        itemsPerPage: requestItemsPerPage.value,
        'requester.id': user.value.id,
        'order[requestedAt]': 'DESC',
        ...(requestMaterialTypeFilter.value ? { 'materialType.id': requestMaterialTypeFilter.value } : {}),
        ...(requestStatusFilter.value ? { 'status': requestStatusFilter.value } : {}),
    };

    uiStore.startLoading();
    MaterialRequestService.getMaterialRequests(requestParams)
        .then((res) => {
            if (res && res.status === 200) {
                totalRequestResults.value = res.data['hydra:totalItems']
                filteredRequests.value = normalizeRequests(res.data['hydra:member']);
            }
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

const normalizeRequests = (requests) => {
    if (!requests) return [];

    return requests.map((request) => ({
        ...request,
        materialType: request.materialType || request.type || null,
    }))
}

const getMaterialsTypes = () => {
    const param = {
        page: 1,
        itemsPerPage: 99999,
    }
    uiStore.startLoading();
    MaterialTypeService.getMaterialTypes(param)
        .then((res) => {
            if (res.status === 200) {
                materialTypes.value = res.data['hydra:member'].map((item) => ({
                    ...item,
                    id: item.id || extractId(item['@id'])
                }))
            }
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

const extractId = (iri) => {
    if (!iri) return null;
    const parts = iri.split('/');
    return Number(parts[parts.length - 1]);
}

function formatDate(input) {
    const date = new Date(input);
    const pad = n => String(n).padStart(2, '0');

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
}

function formatDateReturn(input) {
    const date = new Date(input);
    const pad = n => String(n).padStart(2, '0');

    const temp = formatDate(input)
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${temp} ${hours}:${minutes}`;
}

const returnStatusClass = (material) => {
    if (!material.returnedAt) return "";

    return material.receivedBySystemsAt
        ? "return-completed"
        : "return-pending";
};

const getRequestStatusLabel = (status) => {
    switch (status) {
        case 'approved':
            return 'Aprobada';
        case 'rejected':
            return 'Rechazada';
        case 'fulfilled':
        case 'fullfilled':
            return 'Completada';
        case 'pending':
        default:
            return 'Pendiente';
    }
}

const returnRequestStatusClass = (status) => {
    switch (status) {
        case 'approved':
            return 'return-completed';
        case 'rejected':
            return 'request-rejected';
        case 'fulfilled':
        case 'fullfilled':
            return 'request-fullfilled';
        case 'pending':
        default:
            return 'return-pending';
    }
}


</script>

<template>
    <div>
        <header-title title="Sistemas" />
        <div class="d-flex flex-column flex-sm-row justify-content-between">
            <div class="d-flex flex-row justify-content-start mb-sm-0">
                <IconBlueBox class="iconBlueBox" />
                <div class="title-text pl-4">
                    Entrega de Material
                </div>
            </div>
            <div class="d-flex flex-row justify-content-end justify-content-sm-end">
                <div class="filter-options">
                    <template v-for="(item, index) in filter" :key="index">
                        <span class="option" :class="{ active: item.active }" @click="changeFilter(index)">
                            {{ item.name }}
                        </span>
                        <span v-if="index < filter.length - 1" class="separator">|</span>
                    </template>
                </div>
            </div>
        </div>
        <div class="table table-material table-not-mobile">
            <div v-if="filteredMaterial && filteredMaterial.length > 0">
                <table>
                    <thead>
                        <tr class="justify-content-start gap-2">
                            <th>
                                FECHA DE ENTREGA
                            </th>
                            <th>
                                MATERIAL
                            </th>
                            <th>
                                NUMERO DE SERIE
                            </th>
                            <th>
                                FECHA DEVOLUCIÓN
                            </th>
                            <th class="material-actions-header">
                                ACCIONES
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="material in filteredMaterial" :key="material.id">
                            <td @click="openDialogInfoMaterial(material)" class="pointer">
                                {{ material.assignedAt ? formatDate(material.assignedAt) : '' }}
                            </td>
                            <td @click="openDialogInfoMaterial(material)" class="pointer">
                                <!-- Empleado -->
                                {{ material.material ? material.material.model : '' }}
                            </td>
                            <td @click="openDialogInfoMaterial(material)" class="pointer">
                                <!-- Numero de serie -->
                                {{ material.material ? material.material.serialTag : '' }}
                            </td>
                            <td @click="openDialogInfoMaterial(material)" class="pointer">
                                <!-- Fecha devolución -->
                                {{ material.returnedAt ? formatDateReturn(material.returnedAt) : '' }}
                            </td>
                            <td class="material-actions-cell">
                                <div class="buttons d-inline-flex justify-content-center align-items-center">
                                    <div class="pointer" v-if="!material.confirmedByEmployeeAt">
                                        <button class="btn btn_yellow btn-material"
                                            @click.stop="openDialogConfirmMaterial(material)">
                                            Confirmar entrega
                                        </button>
                                    </div>
                                    <div class="pointer" v-if="!material.returnedAt && material.confirmedByEmployeeAt">
                                        <button class="btn btn_red btn-material"
                                            @click.stop="openDialogReturnMaterial(material)">
                                            Devolver
                                        </button>
                                    </div>
                                    <div v-if="material.returnedAt" :class="returnStatusClass(material)">
                                        {{ material.receivedBySystemsAt ? "Devolución completada" :
                                            "Devolución solicitada" }}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else>
                <h5>No se han encontrado material según los filtros especificados...</h5>
            </div>
        </div>
        <div class="table-mobile">
            <div v-if="filteredMaterial && filteredMaterial.length > 0">
                <div v-for="material in filteredMaterial" :key="material.id">
                    <el-row class="mt-4" @click="openDialogInfoMaterial(material)">
                        <el-card class="card-material-item">
                            <div class="row d-flex">
                                <div class="el-col-xs-24" v-if="material.assignedAt">
                                    <span class="material-item-span">Fecha de entrega: </span> {{
                                        formatDate(material.assignedAt)
                                    }}
                                </div>
                                <div class="el-col-xs-24"
                                    v-if="material && material.material && material.material.model">
                                    <span class="material-item-span">Material: </span>{{ material.material.model }}
                                </div>
                                <div class="el-col-xs-24"
                                    v-if="material && material.material && material.material.serialTag">
                                    <span class="material-item-span">Número de serie: </span> {{
                                        material.material.serialTag }}
                                </div>
                                <div class="el-col-xs-24" v-if="material && material.returnedAt">
                                    <span class="material-item-span">Fecha de devolución: </span>
                                    {{ formatDateReturn(material.returnedAt) }}
                                </div>
                                <div class="buttons d-inline-flex justify-content-end align-content-center mt-2">
                                    <div class="pointer" v-if="!material.confirmedByEmployeeAt">
                                        <button class="btn btn_yellow btn-material"
                                            @click.stop="openDialogConfirmMaterial(material)">
                                            Confirmar entrega
                                        </button>
                                    </div>
                                    <div class="pointer" v-if="!material.returnedAt && material.confirmedByEmployeeAt">
                                        <button class="btn btn_red btn-material"
                                            @click.stop="openDialogReturnMaterial(material)">
                                            Devolver
                                        </button>
                                    </div>
                                    <div v-if="material.returnedAt" :class="returnStatusClass(material)">
                                        {{ material.receivedBySystemsAt ?
                                            "Devolución completada" : "Devolución solicitada" }}
                                    </div>
                                </div>
                            </div>
                        </el-card>
                    </el-row>
                </div>
            </div>
            <div v-else>
                <h5>No se han encontrado material según los filtros especificados...</h5>
            </div>
        </div>
        <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" :total="totalResults"
            :page-size-options="pageSizeOptions" class="tracker-pagination material-pagination"
            @current-change="onMaterialPageChange" @size-change="onMaterialSizeChange" />

        <section class="material-requests-section">
            <div class="d-flex flex-row justify-content-start align-items-center material-requests-title">
                <IconBlueBox class="iconBlueBox" />
                <div class="title-text pl-4">
                    Solicitudes de material
                </div>
            </div>
            <el-row>
                <el-card class="card-search w-100 wback" body-style="padding:0">
                    <div class="row">
                        <div class="col-xl-4 col-md-6 col-lg-4">
                            <div class="form-item w-100 label-common">
                                <label for="request-search-type" class="w-100">Tipo de material</label>
                                <el-select id="request-search-type" v-model="requestMaterialTypeFilter" filterable
                                    clearable placeholder="TODOS" @change="searchRequests">
                                    <el-option v-for="item in materialTypes" :key="item.id || item['@id']"
                                        :label="item.name" :value="item.id" />
                                </el-select>
                            </div>
                        </div>
                        <div class="col-xl-4 col-md-6 col-lg-4">
                            <div class="form-item w-100 label-common pl-5">
                                <label for="request-search-status" class="w-100">Estado</label>
                                <el-select id="request-search-status" v-model="requestStatusFilter" filterable clearable
                                    placeholder="TODOS" @change="searchRequests">
                                    <el-option v-for="item in requestStatusOptions" :key="item.value" :label="item.name"
                                        :value="item.value" />
                                </el-select>
                            </div>
                        </div>
                        <div class="col-xl-4 col-md-12 col-lg-12 request-search-actions">
                            <div class="btn-group-responsive">
                                <button class="btn btn_yellow btn-search btn__search" @click="searchRequests">
                                    Buscar
                                </button>
                                <button class="btn btn_red btn__new" @click="requestMaterial = true">
                                    Nueva solicitud
                                </button>
                            </div>
                        </div>
                    </div>
                </el-card>
            </el-row>
            <div class="table table-material table-not-mobile">
                <div v-if="filteredRequests && filteredRequests.length > 0">
                    <table>
                        <thead>
                            <tr class="justify-content-start gap-2">
                                <th>
                                    FECHA DE SOLICITUD
                                </th>
                                <th>
                                    TIPO
                                </th>
                                <th>
                                    DESCRIPCIÓN
                                </th>
                                <th>
                                    COMENTARIO SISTEMAS
                                </th>
                                <th>
                                    ESTADO
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="request in filteredRequests" :key="request.id">
                                <td>
                                    {{ request.requestedAt ? formatDateReturn(request.requestedAt) : '' }}
                                </td>
                                <td>
                                    {{ request.materialType ? request.materialType.name : '' }}
                                </td>
                                <td class="request-description-cell">
                                    {{ request.description ? request.description : '' }}
                                </td>
                                <td class="request-description-cell">
                                    {{ request.systemsComment ? request.systemsComment : '' }}
                                </td>
                                <td class="request-status-cell">
                                    <span :class="returnRequestStatusClass(request.status)">
                                        {{ getRequestStatusLabel(request.status) }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else>
                    <h5>No se han encontrado solicitudes según los filtros especificados...</h5>
                </div>
            </div>
            <div class="table-mobile">
                <div v-if="filteredRequests && filteredRequests.length > 0">
                    <div v-for="request in filteredRequests" :key="request.id">
                        <el-row class="mt-4">
                            <el-card class="card-material-item">
                                <div class="row d-flex">
                                    <div class="el-col-xs-24" v-if="request.requestedAt">
                                        <span class="material-item-span">Fecha de solicitud: </span>
                                        {{ formatDateReturn(request.requestedAt) }}
                                    </div>
                                    <div class="el-col-xs-24" v-if="request.materialType && request.materialType.name">
                                        <span class="material-item-span">Tipo: </span>{{ request.materialType.name }}
                                    </div>
                                    <div class="el-col-xs-24" v-if="request.description">
                                        <span class="material-item-span">Descripción: </span>{{ request.description }}
                                    </div>
                                    <div class="el-col-xs-24" v-if="request.systemsComment">
                                        <span class="material-item-span">Comentario sistemas: </span>{{
                                            request.systemsComment }}
                                    </div>
                                    <div class="el-col-xs-24 mt-2">
                                        <span :class="returnRequestStatusClass(request.status)">
                                            {{ getRequestStatusLabel(request.status) }}
                                        </span>
                                    </div>
                                </div>
                            </el-card>
                        </el-row>
                    </div>
                </div>
                <div v-else>
                    <h5>No se han encontrado solicitudes según los filtros especificados...</h5>
                </div>
            </div>
            <SharedElementPagination v-model:current-page="requestPage" v-model:page-size="requestItemsPerPage"
                :total="totalRequestResults" :page-size-options="pageSizeOptions"
                class="tracker-pagination material-pagination" @current-change="onRequestPageChange"
                @size-change="onRequestSizeChange" />
        </section>
    </div>
    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="dialogMyMaterial" width="601" :show-close="false" :lock-scroll="true"
        @close="closeDialogMyMaterial">
        <!-- Mi material // Datos material -->
        <MyMaterialDialog v-if="dialogMyMaterial" />
    </el-dialog>
    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="dialogConfirmMaterial" width="601" :show-close="false" :lock-scroll="true"
        @close="closeDialogConfirmMaterial">
        <!-- Confirmar material -->
        <ConfirmMaterialDialog v-if="dialogConfirmMaterial" :material="selectedMaterial"
            @close-dialog="closeDialogConfirmMaterial" />
    </el-dialog>
    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="requestMaterial" width="601" :show-close="false" :lock-scroll="true"
        @close="closeDialogRequestMaterial">
        <!-- Solicitar material -->
        <RequestMaterial v-if="requestMaterial" @close-dialog="closeDialogRequestMaterial" />
    </el-dialog>
    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="requestMaterialReturn" width="601" :show-close="false" :lock-scroll="true"
        @close="closeDialogRequestMaterialReturn">
        <!-- Solicitar devolución -->
        <RequestMaterialReturn v-if="requestMaterialReturn" :material="selectedMaterial"
            @close-dialog="closeDialogRequestMaterialReturn" />
    </el-dialog>
    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="materialDelivery" width="601" :show-close="false" :lock-scroll="true"
        @close="closeDialogInfoMaterial">
        <!-- Ver Material -->
        <MaterialDeliveryDialog v-if="materialDelivery" :material="selectedMaterial" :employee="true"
            @close-dialog="closeDialogInfoMaterial" />
    </el-dialog>
</template>

<style lang="scss" scoped>
.iconBlueBox {
    width: 30px;
    fill: $bg-blue;
}

.filter-options {
    font-weight: bold;
    font-size: 32px;
}

.option {
    cursor: pointer;
    color: #3B47FF;
    /* Azul por defecto */
    margin: 0 0.2em;
}

.option.active {
    color: #FF006E;
    /* Rosa cuando está activo */
}

.separator {
    color: #3B47FF;
    /* Separador siempre azul */
    margin: 0 0.2em;
}

.btn-material {
    width: 15rem;
    font-weight: 600;
}

.material-actions-header,
.material-actions-cell {
    text-align: center;
    vertical-align: middle;
    width: 18rem;
}

.material-actions-cell .buttons {
    width: 100%;
}

.btn__search,
.btn__new {
    width: 200px;
    height: 3em;
}

.btn-group-responsive {
    display: flex;
    gap: 1rem;
    align-items: center;

    @media (max-width: 767px) {
        flex-direction: column;
        align-items: stretch;
        width: 100%;

        button {
            width: 100% !important;
        }
    }
}

.request-search-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.material-pagination {
    justify-content: flex-end;
    width: 100%;
    margin-top: 12px;
}

.material-requests-section {
    margin-top: 32px;
}

.material-requests-title {
    margin-bottom: 12px;
}

@media (max-width: 1594px) and (min-width: 768px) {
    .request-search-actions {
        justify-content: flex-start;
        width: 100%;
        max-width: 100%;
        flex: 0 0 100%;
        margin-top: 1rem;
    }

    .request-search-actions .btn-group-responsive {
        width: 100%;
        justify-content: flex-end;
    }
}

@media (min-width: 1595px) {
    .request-search-actions {
        margin-top: 0;
    }
}

.request-status-cell {
    text-align: center;
    vertical-align: middle;
}

.request-description-cell {
    width: 30%;
    max-width: 30%;
    white-space: normal;
    word-break: break-word;
}

.return-pending,
.return-completed,
.request-rejected,
.request-fullfilled {
    display: inline-block;
    padding: 0.35rem 0.75rem;
    border-radius: 0.375rem;
    font-weight: 600;
    font-size: 0.95rem;
    color: #fff;
    text-align: center;
}

.return-pending {
    background-color: #f1c40f;
}

.return-completed {
    background-color: #27ae60;
}

.request-rejected {
    background-color: #E53935;
}

.request-fullfilled {
    background-color: #107DDE;
}

@media(min-width: 768px) {
    .table-mobile {
        display: none;
    }
}

@media(max-width: 767px) {
    .table-not-mobile {
        display: none;
    }

    .filter-options {
        font-size: 20px;
    }

    .card-material-item {
        padding: 10px 0px;
        border-radius: 16px;
        font-size: 15px;

        .material-item-span {
            color: $bg-blue;
            font-weight: 800;
            font-size: 16px;
            line-height: 100%;
            letter-spacing: 0;

        }
    }

    .material-pagination {
        justify-content: flex-start;
    }

    .material-requests-section {
        margin-top: 24px;
    }

    .card-search {
        margin-top: 12px;
    }

    .card-search:deep(.el-card__body) {
        padding: 16px 12px !important;
    }

    .card-search .row {
        margin-left: 0;
        margin-right: 0;
    }

    .card-search [class*="col-"] {
        padding-left: 0;
        padding-right: 0;
    }

    .card-search .form-item {
        margin-top: 0.75rem !important;
        padding-left: 0 !important;
    }

    .card-search .el-select {
        width: 100% !important;
    }
}
</style>
